// CustomerHoldingsPage — one customer in full, seen from outside.
//
// The portal's face on operations/templates/operations/customer_detail.html,
// which it will replace. Five tables, in the template's order and with the
// template's columns: subscriptions, instances, usage by dimension, the recent
// usage hours, the recent billing submissions. Each keeps its own
// nothing-yet sentence, because "no submissions" and "no usage" mean different
// things about a customer and a shared empty state would say neither.
//
// Named for what it shows rather than for who is looking. It is one customer's
// holdings — the same holdings that customer sees on their own screens, read
// by somebody who is not them. The subscription and instance states read
// through the one presentation rule every surface shares
// (../data/presentation.ts), so an internal agreement says "Internal, not
// billable" here as it does on Fleet rather than the stored
// `pending_activation` nothing will ever activate, and a decommissioned
// instance carries the day it was retired. The provisioning and submission
// states stay the core's own word, because the person reading them is
// correlating against a log or the channel's console.
//
// **No nav entry leads here**, so this page carries the way back that the
// sidebar cannot: it is reached by choosing a customer on All Customers, and
// a sidebar cannot offer a link to a customer nobody has picked yet.
//
// **The two controls on it** are an agreement's billing mode — live, or
// withheld (everything up to the channel call runs, and the call does not) —
// and its kind: a customer's, or our own (internal), which decides whether its
// metering alarms page the operator or the internal topic. Both are offered
// only to a person holding the act-for-customers role, ask for a reason, and
// keep who, when and why on the agreement — shown beside the value whether or
// not the reader may change it.
//
// Reaching it writes exactly ONE row into the see-all trail — the read that
// resolves the customer. Every read after it is scoped to that customer, so it
// is as narrow as one of their own members', which is why one row here and
// five on the screen before it. An identifier nobody holds answers 404,
// indistinguishable from the 404 a caller who may not look receives.

import { ArrowLeft } from 'lucide-react';
import { Fragment, useCallback, useState, type FormEvent } from 'react';
import { Badge } from '@miq/design-system/components/ui/badge';
import { Button } from '@miq/design-system/components/ui/button';
import { Input } from '@miq/design-system/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import { RetiredBadge } from '../components/RetiredBadge';
import { ScreenFailed, ScreenLoading } from '../components/ScreenState';
import { ABSENT, quantityText, usageHourLabel } from '../data/format';
import { onDay, when } from '../data/instants';
import { agreementShown, instanceShown, type Shown } from '../data/presentation';
import {
  readCustomerHoldings,
  setSubscriptionAgreementKind,
  setSubscriptionBillingMode,
  type AgreementKindRecord,
  type BillingModeRecord,
  type CustomerHoldingsScreen,
  type CustomerSubscription,
} from '../data/acrossCustomers';
import { usePortalRead } from '../data/usePortalRead';
import { PortalRequestFailed, type AuthorizedFetch } from '../auth/api';
import { usePortalSession } from '../auth/context';
import { ACCRUING_TONE } from './BillingPage';
import { useDocumentTitle } from '../../lib/documentTitle';

const TITLE = 'Customer';

export function CustomerHoldingsPage({
  publicId,
  onBack,
}: {
  publicId: string;
  onBack: () => void;
}) {
  useDocumentTitle(TITLE);
  // Memoised per customer because `usePortalRead` holds this in a dependency
  // array; an inline arrow would re-run the request on every render and never
  // settle — and every re-run of this one writes another row into the audit
  // trail, so a render loop here would be a loop that forges a reading history.
  const read = useCallback(
    (authorizedFetch: AuthorizedFetch) =>
      readCustomerHoldings(authorizedFetch, publicId),
    [publicId],
  );
  const screen = usePortalRead(read);

  if (screen.status === 'loading') {
    return <ScreenLoading title={TITLE} />;
  }
  if (screen.status === 'failed') {
    return <ScreenFailed title={TITLE} message={screen.message} />;
  }
  return <Holdings screen={screen.data} onBack={onBack} />;
}

/** The core's own word for a state, in the shape the templates gave it. */
function State({ word }: { word: string }) {
  return word ? <Badge variant="outline">{word}</Badge> : <>{ABSENT}</>;
}

/** A state drawn through the shared presentation rule. */
function StateShown({ shown }: { shown: Shown }) {
  return <Badge variant={shown.variant}>{shown.label}</Badge>;
}

/**
 * An agreement's billing mode, and who last set it.
 *
 * Withheld wears the warning tone: nothing failed, but a reader adding up what
 * this customer is billed must see that this agreement is billed nothing. The
 * who, when and why are shown whenever the mode was ever changed, because a
 * withheld agreement with no visible reason is a question the next reader has
 * to go and ask.
 */
function BillingModeCell({ record }: { record: BillingModeRecord }) {
  return (
    <div className="space-y-1">
      {record.mode === 'withheld' ? (
        <Badge variant="outline" className={ACCRUING_TONE}>
          Withheld
        </Badge>
      ) : (
        <Badge variant="outline">Live</Badge>
      )}
      {record.changed_at !== null ? (
        <div className="text-xs text-muted-foreground">
          Since {onDay(record.changed_at)} by {record.changed_by}:{' '}
          <span className="text-foreground">{record.reason}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Change one agreement's billing mode, with a reason.
 *
 * Opened one row at a time and closed on success, as the deployment acts are.
 * The target is always the other mode: there is nothing to choose, and the
 * agreement reference is repeated so the person can see which agreement they
 * are about to stop billing (open question 4's default: no second step).
 */
function ChangeBillingMode({
  subscription,
  onRecorded,
  onCancel,
}: {
  subscription: CustomerSubscription;
  onRecorded: (record: BillingModeRecord) => void;
  onCancel: () => void;
}) {
  const { authorizedFetch } = usePortalSession();
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState('');
  const [working, setWorking] = useState(false);
  const target = subscription.billing_mode.mode === 'withheld' ? 'live' : 'withheld';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setRefusal('');
    setWorking(true);
    try {
      const recorded = await setSubscriptionBillingMode(
        authorizedFetch,
        subscription.public_id,
        target,
        reason,
      );
      onRecorded({
        mode: recorded.billing_mode,
        changed_at: recorded.changed_at,
        changed_by: recorded.changed_by,
        reason: recorded.reason,
      });
    } catch (failure) {
      // The service's own wording, as the deployment acts show it: a 409 is
      // the service saying nothing would change, not a fault.
      setRefusal(
        failure instanceof PortalRequestFailed
          ? failure.message
          : 'That could not be recorded. Check your connection and try again.',
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2 py-2">
      <p className="text-sm">
        {target === 'withheld'
          ? 'Withhold this agreement: its usage is still ledgered and shown, marked withheld, and nothing is sent to the channel. Withheld hours are never billed later.'
          : 'Bill this agreement again from the next hour on. The hours withheld so far stay withheld.'}{' '}
        <span className="font-mono text-xs">{subscription.agreement_ref}</span>
      </p>
      <div>
        <label className="text-xs text-muted-foreground" htmlFor={`why-${subscription.public_id}`}>
          Why. Recorded verbatim against your name, on the agreement.
        </label>
        <Input
          id={`why-${subscription.public_id}`}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="mt-1"
        />
      </div>
      {refusal ? <p className="text-xs font-medium text-destructive">{refusal}</p> : null}
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          variant="default"
          isPending={working}
          isDisabled={!reason.trim()}
        >
          {target === 'withheld' ? 'Withhold this agreement' : 'Bill this agreement'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onPress={onCancel} isDisabled={working}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * An agreement's kind, and who last set it.
 *
 * Shown only when it says something: a customer agreement nobody ever marked
 * is the ordinary case and carries no badge. Internal is neutral, not a
 * warning — the agreement is metered and alarmed as any other, and only the
 * page its faults reach differs.
 */
function AgreementKindCell({ record }: { record: AgreementKindRecord }) {
  if (record.kind === 'customer' && record.changed_at === null) {
    return null;
  }
  return (
    <div className="space-y-1">
      <Badge variant="outline">
        {record.kind === 'internal' ? 'Internal' : 'Customer'}
      </Badge>
      {record.changed_at !== null ? (
        <div className="text-xs text-muted-foreground">
          Since {onDay(record.changed_at)} by {record.changed_by}:{' '}
          <span className="text-foreground">{record.reason}</span>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          On the internal channel, so internal by definition.
        </div>
      )}
    </div>
  );
}

/**
 * Change one agreement's kind, with a reason. The same shape as the billing
 * mode's form: the target is always the other kind.
 */
function ChangeAgreementKind({
  subscription,
  onRecorded,
  onCancel,
}: {
  subscription: CustomerSubscription;
  onRecorded: (record: AgreementKindRecord) => void;
  onCancel: () => void;
}) {
  const { authorizedFetch } = usePortalSession();
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState('');
  const [working, setWorking] = useState(false);
  const target = subscription.agreement_kind.kind === 'internal' ? 'customer' : 'internal';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setRefusal('');
    setWorking(true);
    try {
      const recorded = await setSubscriptionAgreementKind(
        authorizedFetch,
        subscription.public_id,
        target,
        reason,
      );
      onRecorded({
        kind: recorded.agreement_kind,
        changed_at: recorded.changed_at,
        changed_by: recorded.changed_by,
        reason: recorded.reason,
      });
    } catch (failure) {
      setRefusal(
        failure instanceof PortalRequestFailed
          ? failure.message
          : 'That could not be recorded. Check your connection and try again.',
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2 py-2">
      <p className="text-sm">
        {target === 'internal'
          ? 'Mark this agreement as our own: its metering faults still alarm, on the internal topic instead of the operator\'s. Billing is unchanged.'
          : 'Mark this agreement as a customer\'s: its metering faults page the operator again. Billing is unchanged.'}{' '}
        <span className="font-mono text-xs">{subscription.agreement_ref}</span>
      </p>
      <div>
        <label className="text-xs text-muted-foreground" htmlFor={`kind-why-${subscription.public_id}`}>
          Why. Recorded verbatim against your name, on the agreement.
        </label>
        <Input
          id={`kind-why-${subscription.public_id}`}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="mt-1"
        />
      </div>
      {refusal ? <p className="text-xs font-medium text-destructive">{refusal}</p> : null}
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          variant="default"
          isPending={working}
          isDisabled={!reason.trim()}
        >
          {target === 'internal' ? 'Mark as internal' : 'Mark as a customer agreement'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onPress={onCancel} isDisabled={working}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** Which form is open on which agreement: its billing mode, or its kind. */
interface OpenForm {
  subscription: string;
  term: 'mode' | 'kind';
}

function Holdings({
  screen,
  onBack,
}: {
  screen: CustomerHoldingsScreen;
  onBack: () => void;
}) {
  // Which agreement's form is open, and what the SERVICE said it recorded for
  // each one changed here — laid over the rows read on mount, because
  // `usePortalRead` reads once and does not refetch.
  const [changing, setChanging] = useState<OpenForm | null>(null);
  const [recorded, setRecorded] = useState<Record<string, BillingModeRecord>>({});
  const [recordedKinds, setRecordedKinds] = useState<Record<string, AgreementKindRecord>>({});
  const {
    may_act: mayAct,
    customer,
    instances,
    usage_by_dimension: usage,
    recent_usage: recentUsage,
    recent_submissions: submissions,
  } = screen;
  const subscriptions = screen.subscriptions.map((subscription) => ({
    ...subscription,
    billing_mode: recorded[subscription.public_id] ?? subscription.billing_mode,
    agreement_kind: recordedKinds[subscription.public_id] ?? subscription.agreement_kind,
  }));
  const isOpen = (subscription: string, term: OpenForm['term']) =>
    changing?.subscription === subscription && changing.term === term;

  return (
    <div className="space-y-5 p-6">
      <div>
        <Button
          variant="link"
          className="h-auto gap-1.5 p-0 text-sm"
          onPress={onBack}
        >
          <ArrowLeft className="size-3.5" /> All Customers
        </Button>
        <h1 className="mt-2 flex flex-wrap items-center gap-3 text-2xl font-semibold tracking-tight">
          {customer.display_name}
          {customer.retired !== null ? <RetiredBadge retired={customer.retired} /> : null}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          <span className="font-mono text-xs">{customer.public_id}</span> · with
          us since {onDay(customer.created_at)}
        </p>
        {/* The drill-in always answers for a retired customer — the history
            is what it is for — and says so in full here, where there is room
            for the sentence: who decided, and why, in their words. */}
        {customer.retired !== null ? (
          <p className="mt-1 text-sm text-muted-foreground">
            Retired {onDay(customer.retired.at)} by {customer.retired.by}:{' '}
            <span className="text-foreground">{customer.retired.reason}</span>
          </p>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subscriptions</CardTitle>
          <CardDescription>
            The agreements this customer holds, the channel each was sold
            through, whether its usage is billed, and whether it is our own.
            {mayAct
              ? ''
              : ' Changing whether an agreement is billed, or whose it is, needs the act-for-customers role, which this sign-in does not hold.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {subscriptions.length === 0 ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              No subscriptions.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subscription</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Agreement</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Billing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((subscription) => (
                    <Fragment key={subscription.public_id}>
                    <TableRow>
                      <TableCell className="font-mono text-xs">
                        {subscription.public_id}
                      </TableCell>
                      <TableCell>{subscription.channel}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {subscription.agreement_ref || ABSENT}
                      </TableCell>
                      <TableCell>
                        <StateShown shown={agreementShown(subscription)} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {onDay(subscription.started_at)}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          <BillingModeCell record={subscription.billing_mode} />
                          <AgreementKindCell record={subscription.agreement_kind} />
                        </div>
                      </TableCell>
                    </TableRow>
                    {mayAct ? (
                      <TableRow>
                        <TableCell colSpan={6} className="pt-0">
                          {isOpen(subscription.public_id, 'mode') ? (
                            <ChangeBillingMode
                              subscription={subscription}
                              onRecorded={(record) => {
                                setRecorded((known) => ({
                                  ...known,
                                  [subscription.public_id]: record,
                                }));
                                setChanging(null);
                              }}
                              onCancel={() => setChanging(null)}
                            />
                          ) : isOpen(subscription.public_id, 'kind') ? (
                            <ChangeAgreementKind
                              subscription={subscription}
                              onRecorded={(record) => {
                                setRecordedKinds((known) => ({
                                  ...known,
                                  [subscription.public_id]: record,
                                }));
                                setChanging(null);
                              }}
                              onCancel={() => setChanging(null)}
                            />
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onPress={() =>
                                  setChanging({ subscription: subscription.public_id, term: 'mode' })
                                }
                              >
                                {subscription.billing_mode.mode === 'withheld'
                                  ? 'Bill this agreement again'
                                  : 'Withhold billing'}
                              </Button>
                              {/* The internal channel is internal by definition:
                                  there is no kind to set, and the service
                                  would refuse one. */}
                              {subscription.channel === 'internal' ? null : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onPress={() =>
                                    setChanging({ subscription: subscription.public_id, term: 'kind' })
                                  }
                                >
                                  {subscription.agreement_kind.kind === 'internal'
                                    ? 'Mark as a customer agreement'
                                    : 'Mark as internal'}
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : null}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Instances</CardTitle>
          <CardDescription>
            What is running, and the agreement each one runs under.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {instances.length === 0 ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              No instances.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Instance</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Client id</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Under</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {instances.map((instance) => (
                    <TableRow key={instance.public_id}>
                      <TableCell className="font-mono text-xs">
                        {instance.public_id}
                      </TableCell>
                      <TableCell className="font-medium">
                        {instance.name}
                        {instance.retired !== null ? (
                          <div className="mt-1">
                            <RetiredBadge retired={instance.retired} />
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {instance.client_id}
                      </TableCell>
                      <TableCell>
                        <StateShown shown={instanceShown(instance)} />
                      </TableCell>
                      {/* The identifier alone. The subscriptions table above
                          has the channel and the state; repeating them per
                          instance would be two sources for one fact. */}
                      <TableCell className="font-mono text-xs">
                        {instance.subscription_public_id}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Usage by dimension</CardTitle>
          <CardDescription>
            Everything ever recorded for this customer, per dimension.
            Dimensions are not summable — one measures volume, another
            well-days — so there is no total row.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {usage.length === 0 ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              No usage recorded.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dimension</TableHead>
                    <TableHead className="text-right">Total quantity</TableHead>
                    <TableHead className="text-right">Hours recorded</TableHead>
                    <TableHead>Latest hour</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage.map((row) => (
                    <TableRow key={row.dimension}>
                      <TableCell className="font-mono text-xs">
                        {row.dimension}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {quantityText(row.total)}
                      </TableCell>
                      <TableCell className="text-right">{row.hours}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {usageHourLabel(row.latest_hour)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent usage hours</CardTitle>
          <CardDescription>
            The last hours this customer reported, newest first. A window, not
            a ledger export.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {recentUsage.length === 0 ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              Nothing received yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hour</TableHead>
                    <TableHead>Instance</TableHead>
                    <TableHead>Dimension</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Received</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentUsage.map((event, index) => (
                    // Keyed on the row's position because an hour is not
                    // unique on its own — two instances report the same hour,
                    // and one instance reports two dimensions in it. The list
                    // is a fixed window that never reorders under the reader.
                    <TableRow key={index}>
                      <TableCell>{usageHourLabel(event.usage_hour)}</TableCell>
                      <TableCell className="font-medium">{event.instance}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {event.dimension}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {quantityText(event.quantity)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {when(event.received_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent billing submissions</CardTitle>
          <CardDescription>
            What was handed to a sales channel, and what the channel said back.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {submissions.length === 0 ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              No submissions — this customer&apos;s channel may never bill
              (internal), or the cutover has not happened.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Submission</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((submission) => (
                    <TableRow key={submission.public_id}>
                      <TableCell className="font-mono text-xs">
                        {submission.public_id}
                      </TableCell>
                      <TableCell>{submission.channel}</TableCell>
                      <TableCell className="text-right font-medium">
                        {quantityText(submission.quantity_submitted)}
                      </TableCell>
                      {/* Empty means the channel has not answered yet, which
                          is an absence rather than an outcome of nothing. */}
                      <TableCell>
                        <State word={submission.outcome} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {when(submission.submitted_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
